import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SERVICE_CATEGORIES, URGENCIES } from "@/lib/constants";
import { Building2, Loader2, PlusCircle, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { RequestNumber, StatusTimeline } from "@/components/RequestTracker";

const EMPTY = { name: "", email: "", phone: "", property_address: "", service_category: "", problem_description: "", urgency: "Routine", requested_date: "", additional_notes: "" };

export default function PublicRequestService() {
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Enter your name");
    if (!f.email.trim()) return toast.error("Enter your email");
    if (!f.phone.trim()) return toast.error("Enter your phone number");
    if (!f.property_address.trim()) return toast.error("Enter the property address");
    if (!f.service_category) return toast.error("Select a service category");
    if (!f.problem_description.trim()) return toast.error("Describe the problem");
    setLoading(true);
    try {
      const { data } = await api.post("/public/service-request", f);
      toast.success(`Request submitted — ${data.request_number}`);
      setConfirm(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-950 border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="public-req-home-link" className="flex items-center gap-2 text-white">
            <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center"><Building2 className="w-5 h-5" /></div>
            <span className="font-head font-bold text-lg">SJ Property Care</span>
          </Link>
          <Button data-testid="public-req-login" variant="ghost" className="text-white hover:bg-white/10" onClick={() => navigate("/login")}>Sign In</Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="public-req-back">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        {confirm ? (
          <div data-testid="public-request-confirmation" className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 md:p-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-white" /></div>
              <div><h2 className="font-head text-xl font-semibold text-slate-900">Request Received</h2><p className="text-sm text-slate-500">Our team will review your request and reach out shortly.</p></div>
            </div>
            <div className="rounded-xl bg-blue-50 border border-blue-100 p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-xs uppercase tracking-wide text-slate-500">Save your tracking number</span>
                <RequestNumber number={confirm.request_number} urgency={f.urgency} />
              </div>
            </div>
            <div className="mt-6"><div className="text-xs uppercase tracking-wide text-slate-400 mb-3">What happens next</div><StatusTimeline wo={{ status: confirm.status }} /></div>
            <div className="mt-6 flex flex-wrap gap-2">
              <Button data-testid="public-conf-new" onClick={() => { setConfirm(null); setF(EMPTY); }} className="bg-blue-600 hover:bg-blue-700">Submit Another Request</Button>
              <Button data-testid="public-conf-register" variant="outline" onClick={() => navigate("/register/client")}>Create an Account to Track</Button>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center"><PlusCircle className="w-5 h-5 text-white" /></div>
              <div><h2 className="font-head text-xl font-semibold text-slate-900">Request Service</h2><p className="text-sm text-slate-500">Tell us what you need — no account required.</p></div>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Full Name</Label><Input data-testid="preq-name" value={f.name} onChange={set("name")} className="mt-1.5" placeholder="Jane Smith" /></div>
                <div><Label>Phone</Label><Input data-testid="preq-phone" value={f.phone} onChange={set("phone")} className="mt-1.5" placeholder="(555) 123-4567" /></div>
              </div>
              <div><Label>Email</Label><Input data-testid="preq-email" type="email" value={f.email} onChange={set("email")} className="mt-1.5" placeholder="jane@example.com" /></div>
              <div><Label>Property Address</Label><Input data-testid="preq-address" value={f.property_address} onChange={set("property_address")} className="mt-1.5" placeholder="123 Main St, City, State ZIP" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Service Category</Label>
                  <Select value={f.service_category} onValueChange={set("service_category")}>
                    <SelectTrigger data-testid="preq-category" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="max-h-64">{SERVICE_CATEGORIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Urgency</Label>
                  <Select value={f.urgency} onValueChange={set("urgency")}>
                    <SelectTrigger data-testid="preq-urgency" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{URGENCIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Problem Description</Label><Textarea data-testid="preq-description" value={f.problem_description} onChange={set("problem_description")} className="mt-1.5" rows={3} placeholder="Describe the issue and any access details" /></div>
              <div><Label>Preferred Date (optional)</Label><Input data-testid="preq-date" type="date" value={f.requested_date} onChange={set("requested_date")} className="mt-1.5" /></div>
              <div><Label>Additional Notes (optional)</Label><Textarea data-testid="preq-notes" value={f.additional_notes} onChange={set("additional_notes")} className="mt-1.5" /></div>
              <Button data-testid="preq-submit" type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 h-11">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Request"}</Button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
